import { useEffect, useRef, useState } from "react";
import { styled } from "styled-components";
import { useNavigate, useParams } from "react-router";
import { Link } from "react-router-dom";
import { FaGithubAlt, FaLink, FaPlay } from "react-icons/fa";
import { FaRegSquareCheck } from "react-icons/fa6";
import { FiArrowLeft, FiArrowRight, FiExternalLink } from "react-icons/fi";
import { IoDocumentSharp } from "react-icons/io5";

import HomeHeading from "../../../components/HomeHeading";
import projects from "../../../assets/docs/projects.json";

const sectionLabels = {
  key_features: "Key Features",
  learning_joy: "What I Learned",
};

const genericDeploymentHint =
  "This project is deployed as a free instance, so you may encounter slowness or initial startup issues.";

function splitProjectTitle(title = "") {
  const [mainTitle, ...subtitleParts] = title.split(/\s+-\s+/);

  return {
    mainTitle,
    subtitle: subtitleParts.join(" - "),
  };
}

const imageExtensions = ["png", "webp", "jpg", "jpeg"];
const architectureImageNames = ["arch", "architecture", "architecture-diagram"];

function getMediaImageBase(thumbnail = "") {
  const lastSlashIndex = thumbnail.lastIndexOf("/");

  if (lastSlashIndex === -1) {
    return "";
  }

  return thumbnail.slice(0, lastSlashIndex + 1);
}

function canLoadImage(src) {
  return new Promise((resolve) => {
    const image = new Image();

    image.onload = () => resolve(src);
    image.onerror = () => resolve(null);
    image.src = src;
  });
}

async function findNumberedImages(thumbnail) {
  const base = getMediaImageBase(thumbnail);

  if (!base) {
    return [];
  }

  const found = [];

  for (let index = 1; index <= 12; index += 1) {
    const candidates = imageExtensions.map(
      (extension) => `${base}${index}.${extension}`,
    );
    const loaded = await Promise.all(candidates.map(canLoadImage));
    const match = loaded.find(Boolean);

    if (!match) {
      break;
    }

    found.push(match);
  }

  return found;
}

async function findArchitectureImage(thumbnail) {
  const base = getMediaImageBase(thumbnail);

  if (!base) {
    return null;
  }

  const candidates = architectureImageNames.flatMap((name) =>
    imageExtensions.map((extension) => `${base}${name}.${extension}`),
  );
  const loaded = await Promise.all(candidates.map(canLoadImage));

  return loaded.find(Boolean) || null;
}

export default function ProjectDetails() {
  const [data, setData] = useState(null);
  const [isPlay, setIsPlay] = useState(false);
  const [videoUnavailable, setVideoUnavailable] = useState(false);
  const [carouselImages, setCarouselImages] = useState([]);
  const [carouselLoading, setCarouselLoading] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [activeImageLoaded, setActiveImageLoaded] = useState(false);
  const [architectureImage, setArchitectureImage] = useState(null);
  const [architectureImageLoading, setArchitectureImageLoading] =
    useState(false);
  const [architectureImageLoaded, setArchitectureImageLoaded] = useState(false);

  const playerRef = useRef(null);

  const param = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const found = projects.find(
      (item) =>
        item.nav && item.nav.toLowerCase() === param.project?.toLowerCase(),
    );

    if (!found) {
      navigate("/projects");
      return;
    }

    setData(found);
    setIsPlay(false);
    setVideoUnavailable(false);
    setCarouselImages([]);
    setCarouselLoading(false);
    setActiveImageIndex(0);
    setActiveImageLoaded(false);
    setArchitectureImage(null);
    setArchitectureImageLoading(false);
    setArchitectureImageLoaded(false);
  }, [navigate, param.project]);

  useEffect(() => {
    if (!data?.thumbnail) {
      return undefined;
    }

    let mounted = true;

    setCarouselLoading(true);
    setActiveImageLoaded(false);

    findNumberedImages(data.thumbnail).then((images) => {
      if (!mounted) {
        return;
      }

      setCarouselImages(images);
      setActiveImageIndex(0);
      setCarouselLoading(false);
    });

    return () => {
      mounted = false;
    };
  }, [data?.thumbnail]);

  useEffect(() => {
    if (!data?.thumbnail) {
      return undefined;
    }

    let mounted = true;

    setArchitectureImageLoading(true);
    setArchitectureImage(null);
    setArchitectureImageLoaded(false);

    findArchitectureImage(data.thumbnail).then((image) => {
      if (!mounted) {
        return;
      }

      setArchitectureImage(image);
      setArchitectureImageLoading(false);
    });

    return () => {
      mounted = false;
    };
  }, [data?.thumbnail]);

  const disableImage = (event) => event.preventDefault();

  const playVideo = () => {
    if (!playerRef.current) {
      return;
    }

    setIsPlay(true);
    playerRef.current.muted = true;
    playerRef.current.volume = 0;
    playerRef.current.play();
  };

  const needsCarousel = !data?.video || videoUnavailable;
  const showCarousel =
    needsCarousel && carouselImages.length > 0 && !carouselLoading;
  const showMediaSkeleton = needsCarousel && carouselLoading;

  const goToPreviousImage = () => {
    setActiveImageIndex((current) =>
      current === 0 ? carouselImages.length - 1 : current - 1,
    );
  };

  const goToNextImage = () => {
    setActiveImageIndex((current) =>
      current === carouselImages.length - 1 ? 0 : current + 1,
    );
  };

  useEffect(() => {
    setActiveImageLoaded(false);
  }, [activeImageIndex, carouselImages]);

  if (!data) {
    return null;
  }

  const title = data.title || data.name;
  const { mainTitle, subtitle } = splitProjectTitle(title);
  const mediaPoster = data.thumbnail;
  const portfolioSections =
    data.content?.filter((item) => {
      const key = Object.keys(item)[0];

      return key !== "tech_stack";
    }) || [];

  const externalLinks = [
    {
      label: "GitHub",
      href: data.github,
      icon: <FaGithubAlt />,
    },
    {
      label: "Docs",
      href: data.documentation,
      icon: <IoDocumentSharp />,
    },
    {
      label: data.type === "game" ? "Play" : "Live",
      href: data.deployed_url,
      icon: <FaLink />,
      hint: data.deployed_url ? genericDeploymentHint : null,
    },
  ].filter((item) => item.href);

  return (
    <Container>
      <div className="page-nav-row">
        <Link
          className="page-nav-button"
          to="/projects"
          aria-label="Back to projects"
        >
          <FiArrowLeft />
        </Link>
      </div>

      <Hero>
        <div className="hero-title">
          <HomeHeading>{mainTitle}</HomeHeading>
          {subtitle && <p className="title-subtitle">{subtitle}</p>}
        </div>

        <div className="hero-intro">
          <p className="description">{data.what || data.description}</p>

          <aside className="project-stack">
            {data.nutshell?.length > 0 && (
              <div className="quick-facts">
                <span>Stack</span>
                <div>
                  {data.nutshell.map((item) => (
                    <small key={item}>{item}</small>
                  ))}
                </div>
              </div>
            )}

            {externalLinks.length > 0 && (
              <div className="actions">
                {externalLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(event) => link.hint && event.preventDefault()}
                  >
                    {link.icon}
                    <span>{link.label}</span>
                    <FiExternalLink />

                    {link.hint && (
                      <div className="tooltip">
                        <p>{link.hint}</p>
                        <button
                          type="button"
                          onClick={() => window.open(link.href, "_blank")}
                        >
                          Run
                        </button>
                      </div>
                    )}
                  </a>
                ))}
              </div>
            )}
          </aside>
        </div>
      </Hero>

      {data.video && !showCarousel && (
        <MediaBlock>
          <div className="player-wrap">
            <div className={`player-cover ${isPlay ? "playing" : ""}`}>
              <img
                src={mediaPoster}
                onContextMenu={disableImage}
                onDragStart={disableImage}
                alt={`${title} preview`}
              />
              <div className="cover" />
              <button onClick={playVideo} type="button" title="Play">
                <FaPlay />
              </button>
            </div>

            <video
              ref={playerRef}
              className="player"
              src={data.video}
              width="100%"
              height="100%"
              controls
              onClick={(event) => event.preventDefault()}
              onEnded={() => setIsPlay(false)}
              onError={() => setVideoUnavailable(true)}
            />
          </div>
        </MediaBlock>
      )}

      {showMediaSkeleton && (
        <MediaBlock>
          <div className="media-skeleton" aria-hidden="true" />
        </MediaBlock>
      )}

      {showCarousel && (
        <MediaBlock>
          <div
            className={`image-carousel ${
              activeImageLoaded ? "image-ready" : ""
            }`}
          >
            {!activeImageLoaded && (
              <div className="media-skeleton" aria-hidden="true" />
            )}

            <img
              src={carouselImages[activeImageIndex]}
              alt={`${title} screenshot ${activeImageIndex + 1}`}
              onContextMenu={disableImage}
              onDragStart={disableImage}
              onLoad={() => setActiveImageLoaded(true)}
            />

            {carouselImages.length > 1 && (
              <>
                <button
                  className="carousel-button previous"
                  type="button"
                  onClick={goToPreviousImage}
                  aria-label="Previous screenshot"
                >
                  <FiArrowLeft />
                </button>
                <button
                  className="carousel-button next"
                  type="button"
                  onClick={goToNextImage}
                  aria-label="Next screenshot"
                >
                  <FiArrowRight />
                </button>

                <div className="carousel-count">
                  {String(activeImageIndex + 1).padStart(2, "0")} /{" "}
                  {String(carouselImages.length).padStart(2, "0")}
                </div>
              </>
            )}
          </div>
        </MediaBlock>
      )}

      {portfolioSections.length > 0 && (
        <Article>
          {portfolioSections.map((item) => {
            const key = Object.keys(item)[0];
            const sectionTitle =
              sectionLabels[key] || key.replaceAll("_", " ");

            return (
              <section key={key}>
                <div className="section-heading">
                  <FaRegSquareCheck />
                  <h2>{sectionTitle}</h2>
                </div>

                <div
                  className="content-wrap"
                  dangerouslySetInnerHTML={{ __html: item[key] }}
                />

                {key === "key_features" &&
                  (architectureImageLoading || architectureImage) && (
                    <div
                      className={`architecture-diagram ${
                        architectureImageLoaded ? "image-ready" : ""
                      }`}
                    >
                      {(!architectureImage || !architectureImageLoaded) && (
                        <div
                          className="diagram-skeleton"
                          aria-hidden="true"
                        />
                      )}

                      {architectureImage && (
                        <img
                          src={architectureImage}
                          alt={`${title} architecture diagram`}
                          onContextMenu={disableImage}
                          onDragStart={disableImage}
                          onLoad={() => setArchitectureImageLoaded(true)}
                        />
                      )}
                    </div>
                  )}
              </section>
            );
          })}
        </Article>
      )}
    </Container>
  );
}

const Container = styled.div`
  position: relative;

  width: 100%;
  min-height: 100vh;
  padding-bottom: 40px;
  font-family: "Poppins", sans-serif;

  .page-nav-row {
    width: 100%;
    max-width: 1120px;
    margin: 0 auto 48px;
  }

  .page-nav-button {
    width: 42px;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;

    border: 1px solid #3d3d3d;
    border-radius: 50%;
    background: rgba(14, 14, 14, 0.72);
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);

    color: #ababab;
    text-decoration: none;

    transition:
      border-color 0.2s ease,
      color 0.2s ease,
      transform 0.2s ease;

    &:hover {
      border-color: #fff;
      color: #fff;
      transform: translateX(-3px);
    }
  }

  @media (max-width: 760px) {
    .page-nav-row {
      margin-bottom: 34px;
    }
  }
`;

const Hero = styled.header`
  position: relative;
  z-index: 5;

  width: 100%;
  max-width: 1120px;
  margin: 0 auto;

  .hero-title {
    width: 100%;
  }

  .title-subtitle {
    max-width: 900px;
    margin: -16px 0 0 0;
    color: rgba(255, 255, 255, 0.62);
    font-family: "Space Mono", monospace;
    font-size: clamp(28px, 4vw, 52px);
    font-weight: 400;
    line-height: 1.25;
    user-select: none;
  }

  .hero-intro {
    display: grid;
    grid-template-columns: minmax(0, 0.9fr) minmax(250px, 360px);
    gap: clamp(34px, 6vw, 80px);
    align-items: start;
    margin-top: 70px;
  }

  .description {
    max-width: 680px;
    margin: 0 0 0 0;
    color: #ababab;
    font-size: clamp(15px, 1.8vw, 19px);
    font-weight: 300;
    line-height: 1.7;
  }

  .actions {
    position: relative;
    z-index: 10;

    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 24px;
    padding-top: 22px;
    border-top: 1px solid #2b2b2b;

    a {
      position: relative;
      min-height: 42px;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 0 14px;
      border: 1px solid #3d3d3d;
      border-radius: 8px;
      color: #ababab;
      background: rgb(14, 14, 14);
      font-size: 13px;
      font-weight: 300;
      text-decoration: none;
      transition:
        border-color 0.2s ease,
        color 0.2s ease,
        transform 0.2s ease;

      &:hover {
        border-color: #fff;
        color: #fff;
        transform: translateY(-1px);
      }

      &:hover .tooltip {
        opacity: 1;
        visibility: visible;
        transform: translateY(0);
      }
    }
  }

  .tooltip {
    position: absolute;
    top: calc(100% + 12px);
    left: 0;
    z-index: 100;
    width: min(280px, 80vw);
    padding: 14px;
    border: 1px solid #3d3d3d;
    border-radius: 8px;
    background: #111;
    box-shadow: 0 18px 40px rgba(0, 0, 0, 0.45);
    opacity: 0;
    visibility: hidden;
    transform: translateY(-4px);
    transition:
      opacity 0.2s ease,
      transform 0.2s ease,
      visibility 0.2s ease;
    cursor: auto;

    p {
      margin: 0;
      color: #bdbdbd;
      font-size: 12px;
      line-height: 1.6;
    }

    button {
      display: block;
      margin: 12px 0 0 auto;
      padding: 5px 12px;
      border: 1px solid #fff;
      border-radius: 6px;
      background: #fff;
      color: #000;
      font-size: 12px;
      cursor: pointer;
    }
  }

  .project-stack {
    padding-top: 2px;
  }

  .quick-facts {
    > span {
      display: block;
      margin-bottom: 16px;
      color: #858585;
      font-size: 12px;
      font-weight: 300;
      letter-spacing: 1.3px;
      text-transform: uppercase;
    }

    div {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
    }

    small {
      border: 1px solid #3d3d3d;
      border-radius: 999px;
      padding: 7px 11px;
      color: #d7d7d7;
      background: rgb(14, 14, 14);
      font-size: 12px;
      font-weight: 300;
      line-height: 1;
    }
  }

  @media (max-width: 880px) {
    .hero-intro {
      grid-template-columns: 1fr;
      margin-top: 34px;
    }

    .description {
      font-size: 15px;
      line-height: 1.75;
    }

    .title-subtitle {
      margin-top: -4px;
      font-size: clamp(22px, 7vw, 34px);
      line-height: 1.3;
    }
  }
`;

const MediaBlock = styled.div`
  position: relative;
  z-index: 1;

  width: 100%;
  max-width: 1120px;
  margin: 72px auto 0;

  .player-wrap {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    border: 1px solid #303030;
    border-radius: 10px;
    background: #111;
  }

  .player-cover,
  .player {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }

  .player-cover {
    z-index: 2;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      user-select: none;
      pointer-events: none;
    }

    .cover {
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.42);
    }

    button {
      position: absolute;
      top: 50%;
      left: 50%;
      width: 64px;
      height: 52px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 0;
      border-radius: 8px;
      background: #fff;
      color: #111;
      transform: translate(-50%, -50%);
      cursor: pointer;
      transition:
        background 0.2s ease,
        transform 0.2s ease;

      &:hover {
        background: #f2f2f2;
        transform: translate(-50%, -50%) scale(1.04);
      }
    }
  }

  .player-cover.playing {
    visibility: hidden;
  }

  .player {
    z-index: 1;
  }

  .media-skeleton {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    border: 1px solid #303030;
    border-radius: 10px;
    background:
      linear-gradient(110deg, transparent 0%, rgba(255, 255, 255, 0.055) 50%, transparent 100%),
      linear-gradient(180deg, rgba(255, 255, 255, 0.035), rgba(255, 255, 255, 0.012));

    &::before {
      content: "";
      position: absolute;
      inset: 0;
      background: linear-gradient(
        100deg,
        transparent 0%,
        rgba(255, 255, 255, 0.075) 42%,
        transparent 84%
      );
      transform: translateX(-100%);
      animation: mediaSkeleton 1.35s ease-in-out infinite;
    }
  }

  @keyframes mediaSkeleton {
    100% {
      transform: translateX(100%);
    }
  }

  .image-carousel {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    border: 1px solid #303030;
    border-radius: 10px;
    background: #111;

    img {
      position: relative;
      z-index: 0;
      width: 100%;
      height: 100%;
      display: block;
      object-fit: cover;
      opacity: 0;
      user-select: none;
      pointer-events: none;
      transition: opacity 0.28s ease;
    }
  }

  .image-carousel.image-ready img {
    opacity: 1;
  }

  .image-carousel .media-skeleton {
    position: absolute;
    inset: 0;
    z-index: 1;
    border: 0;
    border-radius: 0;
    aspect-ratio: auto;
  }

  .carousel-button {
    position: absolute;
    top: 50%;
    z-index: 3;
    width: 42px;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(255, 255, 255, 0.28);
    border-radius: 50%;
    background: rgba(14, 14, 14, 0.72);
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    color: #fff;
    cursor: pointer;
    transform: translateY(-50%);
    transition:
      border-color 0.2s ease,
      background 0.2s ease,
      transform 0.2s ease;

    &:hover {
      border-color: rgba(255, 255, 255, 0.62);
      background: rgba(20, 20, 20, 0.9);
      transform: translateY(-50%) scale(1.04);
    }

    svg {
      font-size: 18px;
    }
  }

  .carousel-button.previous {
    left: 18px;
  }

  .carousel-button.next {
    right: 18px;
  }

  .carousel-count {
    position: absolute;
    right: 18px;
    bottom: 16px;
    z-index: 3;
    padding: 6px 10px;
    border: 1px solid rgba(255, 255, 255, 0.16);
    border-radius: 999px;
    background: rgba(14, 14, 14, 0.68);
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    color: #d7d7d7;
    font-family: "Space Mono", monospace;
    font-size: 12px;
    font-weight: 300;
    line-height: 1;
  }

  @media (max-width: 760px) {
    margin-top: 48px;

    .carousel-button {
      width: 36px;
      height: 36px;
    }

    .carousel-button.previous {
      left: 12px;
    }

    .carousel-button.next,
    .carousel-count {
      right: 12px;
    }

    .carousel-count {
      bottom: 12px;
      font-size: 10px;
    }
  }
`;

const Article = styled.article`
  width: 100%;
  max-width: 1120px;
  margin: 72px auto 0;

  section {
    display: grid;
    grid-template-columns: 190px minmax(0, 1fr);
    column-gap: clamp(24px, 4vw, 52px);
    row-gap: 0;
    padding: 40px 0;
    border-top: 1px solid #2b2b2b;
  }

  section:first-of-type {
    border-top: 0;
  }

  .section-heading {
    display: flex;
    align-items: center;
    gap: 12px;
    align-self: start;
    color: #fff;

    svg {
      flex-shrink: 0;
      color: #858585;
      font-size: 16px;
    }

    h2 {
      margin: 0;
      color: #fff;
      font-size: 18px;
      font-weight: 400;
      line-height: 1.35;
    }
  }

  .content-wrap {
    color: #ababab;

    ul {
      display: grid;
      gap: 22px;
      margin: 0;
      padding: 0;
      list-style: none;
      counter-reset: project-point;
    }

    li {
      display: grid;
      grid-template-columns: 54px minmax(0, 1fr);
      align-items: baseline;
      color: #d7d7d7;
      font-size: 17px;
      font-weight: 300;
      line-height: 1.45;
      counter-increment: project-point;

      &::before {
        content: counter(project-point, decimal-leading-zero);
        color: #686868;
        font-family: "Space Mono", monospace;
        font-size: 12px;
        font-weight: 400;
        letter-spacing: 0;
      }
    }

    p {
      margin: 0 0 14px;
      color: #ababab;
      font-size: 17px;
      font-weight: 300;
      line-height: 1.65;
    }

    strong {
      color: #fff;
      font-weight: 400;
    }
  }

  .architecture-diagram {
    position: relative;
    grid-column: 1 / -1;
    justify-self: center;
    width: 92%;
    height: clamp(260px, 36vw, 420px);
    margin: 20px 0;
    overflow: hidden;

    img {
      position: absolute;
      top: 50%;
      left: 0;
      z-index: 0;
      width: 100%;
      height: auto;
      display: block;
      opacity: 0;
      user-select: none;
      pointer-events: none;
      transform: translateY(-50%);
      transition: opacity 0.28s ease;
    }
  }

  .architecture-diagram.image-ready img {
    opacity: 1;
  }

  .diagram-skeleton {
    position: absolute;
    inset: 0;
    z-index: 1;
    overflow: hidden;
    background:
      linear-gradient(110deg, transparent 0%, rgba(255, 255, 255, 0.055) 50%, transparent 100%),
      linear-gradient(180deg, rgba(255, 255, 255, 0.035), rgba(255, 255, 255, 0.012));

    &::before {
      content: "";
      position: absolute;
      inset: 0;
      background: linear-gradient(
        100deg,
        transparent 0%,
        rgba(255, 255, 255, 0.075) 42%,
        transparent 84%
      );
      transform: translateX(-100%);
      animation: diagramSkeleton 1.35s ease-in-out infinite;
    }
  }

  @keyframes diagramSkeleton {
    100% {
      transform: translateX(100%);
    }
  }

  @media (max-width: 820px) {
    margin-top: 54px;

    section {
      grid-template-columns: 1fr;
      column-gap: 20px;
      row-gap: 0;
      padding: 34px 0;
    }

    .architecture-diagram {
      grid-column: 1;
      margin-top: 0;
      height: clamp(220px, 58vw, 320px);
    }

    .content-wrap {
      ul {
        gap: 18px;
      }

      li {
        grid-template-columns: 42px minmax(0, 1fr);
      }

      li::before {
        font-size: 11px;
      }

      li,
      p {
        font-size: 14px;
        line-height: 1.75;
      }
    }
  }
`;
